import { useEffect, useState } from "react";

type EffectiveType = "slow-2g" | "2g" | "3g" | "4g" | "unknown";

interface NetworkQuality {
  effectiveType: EffectiveType;
  saveData: boolean;
  /** true when we should aggressively preload (fast wifi / 4g, no data-saver) */
  isFast: boolean;
  /** true when we should minimize video preloading (2g, slow-2g, or save-data on) */
  isSlow: boolean;
}

interface NavigatorConnection {
  effectiveType?: EffectiveType;
  saveData?: boolean;
  addEventListener?: (t: string, l: () => void) => void;
  removeEventListener?: (t: string, l: () => void) => void;
}

const readConnection = (): NetworkQuality => {
  if (typeof navigator === "undefined") {
    return { effectiveType: "unknown", saveData: false, isFast: true, isSlow: false };
  }
  const conn = (navigator as unknown as { connection?: NavigatorConnection }).connection;
  const effectiveType = (conn?.effectiveType ?? "unknown") as EffectiveType;
  const saveData = Boolean(conn?.saveData);
  const isSlow = saveData || effectiveType === "slow-2g" || effectiveType === "2g";
  const isFast = !isSlow && (effectiveType === "4g" || effectiveType === "unknown");
  return { effectiveType, saveData, isFast, isSlow };
};

/**
 * Reactive Network Information hook. Returns effectiveType + saveData plus
 * `isFast` / `isSlow` bands used to pick video preload strategy.
 * Falls back to fast defaults on browsers that don't expose navigator.connection.
 */
export function useNetworkQuality(): NetworkQuality {
  const [state, setState] = useState<NetworkQuality>(() => readConnection());

  useEffect(() => {
    const conn = (navigator as unknown as { connection?: NavigatorConnection }).connection;
    if (!conn?.addEventListener) return;
    const update = () => setState(readConnection());
    conn.addEventListener("change", update);
    return () => conn.removeEventListener?.("change", update);
  }, []);

  return state;
}